/**
 * @license
 * Copyright Piotr Stępniewski <k3nsei.pl@gmail.com> All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://raw.githubusercontent.com/k3nsei/ng-in-viewport/stable/LICENSE
 */

import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { InViewportConfig, InViewportConfigOptions } from './in-viewport-config';
import { InViewportService } from './in-viewport.service';

export const InViewportMetadata = Symbol('InViewportMetadata');

@Directive({ selector: '[inViewport]' })
export class InViewportDirective implements AfterViewInit, OnDestroy {
  @Input('inViewportOptions')
  public set options(value: InViewportConfigOptions) {
    this.config = new InViewportConfig(value);
  }

  @Output() public readonly inViewportAction = new EventEmitter<any>();

  private config = new InViewportConfig();

  private readonly destroyed$ = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private readonly elementRef: ElementRef,
    private readonly inViewport: InViewportService,
  ) {}

  public ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.inViewport.register(this.elementRef.nativeElement, this.config);
      this.inViewport.trigger$
        .pipe(
          filter((entry: IntersectionObserverEntry) => entry && entry.target === this.elementRef.nativeElement),
          takeUntil(this.destroyed$),
        )
        .subscribe((entry: IntersectionObserverEntry) => this.emitAction(entry, false));
    } else {
      this.emitAction(undefined, true);
    }
  }

  public ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.inViewport.unregister(this.elementRef.nativeElement, this.config);
    }

    if (this.destroyed$ && !this.destroyed$.closed) {
      this.destroyed$.next();
      this.destroyed$.complete();
    }
  }

  private check(entry: IntersectionObserverEntry | undefined, force: boolean) {
    const isVisible = () => {
      const { isIntersecting = true, intersectionRatio = 1 } = entry ?? {};

      const partiallyVisible = isIntersecting || intersectionRatio > 0;
      const completelyVisible = intersectionRatio >= 1;
      return this.config.partial ? partiallyVisible : completelyVisible;
    };
    const visible = force || !entry || isVisible();
    return { [InViewportMetadata]: { entry }, target: this.elementRef.nativeElement, visible };
  }

  private emitAction(entry: IntersectionObserverEntry | undefined, force: boolean): void {
    const event = this.config.checkFn
      ? this.config.checkFn(entry, { force, config: this.config })
      : this.check(entry, force);
    this.inViewportAction.emit(event);
  }
}
